import { FormBlockInstance, LocalizedText } from '../api-types';

export const SHIFT_FIT_TYPE = 'shift_fit';
export const SHIFT_FIT_TYPE_VERSION = 1;

/**
 * The separator pro-core composes a cell key with — `mon__early`.
 *
 * Reserved server-side for exactly this (`FormKeys.COMPOSED_SEPARATOR`), so a composed pair can never
 * be confused with an authored key. Duplicated here because the answer is a list of composed keys and
 * getting the separator wrong would silently produce cells the server refuses.
 */
const COMPOSED_SEPARATOR = '__';

export interface ShiftFitColumn {
  key: string;
  label: LocalizedText | null;
}

export interface ShiftFitSlot {
  key: string;
  label: LocalizedText | null;
  /** `HH:mm` or `HH:mm:ss`, both of which pro-core accepts. Null when the operator set no times. */
  startTime: string | null;
  endTime: string | null;
}

export type ShiftFitRuleEffect = 'WARNING' | 'BLOCKING';

/**
 * A cap on the total time selected.
 *
 * `WARNING` announces and lets the answer through; `BLOCKING` is refused server-side. The effect is
 * operator-owned on purpose — nothing here may decide on its own that someone's availability is a hard
 * exclusion — so the renderer reads it rather than assuming one.
 */
export interface ShiftFitDurationRule {
  effect: ShiftFitRuleEffect;
  minutes: number;
  message: LocalizedText | null;
}

export interface ShiftFitConfig {
  columns: ShiftFitColumn[];
  slots: ShiftFitSlot[];
  /** Hard limits in CELLS, enforced by the server, so the renderer must respect them too. */
  minSelectedCells: number | null;
  maxSelectedCells: number | null;
  /** Adds up the selected slots. Requires a start and an end on every slot to mean anything. */
  durationSummary: boolean;
  showSlotTimes: boolean;
  durationRule: ShiftFitDurationRule | null;
}

/** The answer shape pro-core's `shift_fit@1` validates: composed cell keys, nothing else. */
export interface ShiftFitAnswer {
  selectedCells: string[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asLocalizedText = (value: unknown): LocalizedText | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

const asPositiveInt = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : null;

const asTime = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;

const readDurationRule = (rules: unknown): ShiftFitDurationRule | null => {
  if (!Array.isArray(rules)) {
    return null;
  }
  const rule = rules
    .map(asRecord)
    .find(entry => entry.type === 'MAXIMUM_SELECTED_DURATION');
  if (!rule) {
    return null;
  }
  const minutes = asPositiveInt(rule.minutes);
  // A limit with no number bounds nothing, so it is read as no limit rather than as zero.
  if (minutes === null) {
    return null;
  }
  return {
    effect: rule.effect === 'BLOCKING' ? 'BLOCKING' : 'WARNING',
    minutes,
    message: asLocalizedText(rule.message)
  };
};

/**
 * Read a stored config into the shape this renderer needs.
 *
 * Tolerant for the same reason every reader here is: the definition was normalized and validated by
 * pro-core before it was stored, so the fields are expected — but a renderer that throws on a surprise
 * takes the whole form down for the respondent, and a grid rendered with fewer columns than intended is
 * still refused at submit.
 *
 * `jobDemandSnapshot` is deliberately NOT read. pro-core can carry the shift pattern a posting
 * advertises, and this app does not draw it: a cell means "I can work this" and nothing else. A bare
 * number beside a tappable square reads as hours, or a rank, or how many others already took it.
 */
export const readShiftFitConfig = (
  block: FormBlockInstance
): ShiftFitConfig => {
  const config = asRecord(block.config);
  const data = asRecord(config.data);
  const interaction = asRecord(config.interaction);
  const features = asRecord(config.features);
  const presentation = asRecord(config.presentation);

  const columns: ShiftFitColumn[] = (
    Array.isArray(data.columns) ? data.columns : []
  )
    .map(asRecord)
    .filter(column => typeof column.key === 'string' && column.key.length > 0)
    .map(column => ({
      key: column.key as string,
      label: asLocalizedText(column.label)
    }));

  const slots: ShiftFitSlot[] = (Array.isArray(data.slots) ? data.slots : [])
    .map(asRecord)
    .filter(slot => typeof slot.key === 'string' && slot.key.length > 0)
    .map(slot => ({
      key: slot.key as string,
      label: asLocalizedText(slot.label),
      startTime: asTime(slot.startTime),
      endTime: asTime(slot.endTime)
    }));

  return {
    columns,
    slots,
    minSelectedCells: asPositiveInt(interaction.minSelectedCells),
    maxSelectedCells: asPositiveInt(interaction.maxSelectedCells),
    durationSummary: features.durationSummary === true,
    showSlotTimes: presentation.showSlotTimes === true,
    durationRule: readDurationRule(config.rules)
  };
};

export const readShiftFitAnswer = (answer: unknown): ShiftFitAnswer | null => {
  const record = asRecord(answer);
  if (!Array.isArray(record.selectedCells)) {
    return null;
  }
  return {
    selectedCells: record.selectedCells.filter(
      (cell): cell is string => typeof cell === 'string' && cell.length > 0
    )
  };
};

/** The composed key for one cell. The server composes it the same way and matches on the whole string. */
export const cellKey = (columnKey: string, slotKey: string): string =>
  `${columnKey}${COMPOSED_SEPARATOR}${slotKey}`;

/**
 * How long one slot lasts, in minutes.
 *
 * An end before a start is read as crossing midnight, because a night shift is 22:00–06:00 and refusing
 * it would be refusing the shift. That is pro-core's own rule, and a summary computed differently here
 * would disagree with the number the server stores.
 */
export const slotMinutes = (slot: ShiftFitSlot): number => {
  const from = parseTime(slot.startTime);
  const to = parseTime(slot.endTime);
  if (from === null || to === null) {
    return 0;
  }
  const span = to - from;
  return span >= 0 ? span : span + 24 * 60;
};

const parseTime = (value: string | null): number | null => {
  if (!value) {
    return null;
  }
  const [hours, minutes] = value.split(':');
  const h = Number(hours);
  const m = Number(minutes);
  if (!Number.isInteger(h) || !Number.isInteger(m)) {
    return null;
  }
  return h * 60 + m;
};

/**
 * The total time the selected cells add up to, in minutes.
 *
 * The selection is turned into a `Set` once rather than scanned per cell. The builder caps a grid at
 * 14 columns by 6 slots, so the difference at that size is nothing — but this renderer does not get
 * to assume the authoring tool's limits, and the cost is paid on every render AND inside
 * `isShiftFitAnswered`, which the runner calls for each block whenever any answer changes.
 */
export const selectedMinutes = (
  selected: string[],
  config: ShiftFitConfig
): number => {
  const chosen = new Set(selected);
  return config.slots.reduce((total, slot) => {
    const taken = config.columns.filter(column =>
      chosen.has(cellKey(column.key, slot.key))
    ).length;
    return total + taken * slotMinutes(slot);
  }, 0);
};

/**
 * Whether an answer is complete enough to move on.
 *
 * Mirrors the server's rules and nothing more. A BLOCKING duration limit counts, because the server
 * refuses the answer and walking someone into that submit is the thing this check exists to prevent; a
 * WARNING one does not, because an answer over it is valid and gating Next on it would silently convert
 * an announcement into a rule.
 */
export const isShiftFitAnswered = (
  answer: unknown,
  config: ShiftFitConfig
): boolean => {
  const parsed = readShiftFitAnswer(answer);
  if (!parsed || parsed.selectedCells.length === 0) {
    return false;
  }
  const { minSelectedCells, maxSelectedCells, durationRule } = config;
  const count = parsed.selectedCells.length;
  if (minSelectedCells !== null && count < minSelectedCells) {
    return false;
  }
  if (maxSelectedCells !== null && count > maxSelectedCells) {
    return false;
  }
  if (durationRule?.effect === 'BLOCKING') {
    return (
      selectedMinutes(parsed.selectedCells, config) <= durationRule.minutes
    );
  }
  return true;
};

/** Add or remove one cell. Order is not meaningful, so a toggle is a plain add-or-drop. */
export const toggleCell = (selected: string[], key: string): string[] =>
  selected.includes(key)
    ? selected.filter(entry => entry !== key)
    : [...selected, key];
